/**
 * TileHeaderSlot - Renders controls in the tile-header location.
 *
 * This slot appears above each individual tile (in the title area) and is
 * typically used to show dimension labels like run name, step, or index.
 */

import React from 'react';

import {
  type MediaBrowserPanelProps,
  type MediaConfigWithDefaults,
} from '../../Component';
import {type StepMeta} from '../../types';
import {ControlRenderer} from '../renderers/ControlRenderer';
import {type ControlDefinition, type GridCell} from '../types';

export type TileHeaderSlotProps = {
  controls: ControlDefinition[];
  cell: GridCell;
  disableRunLinks?: boolean;
  config: MediaConfigWithDefaults;
  updateConfig: MediaBrowserPanelProps['updateConfig'];
  stepMeta: StepMeta;
  columnIndex?: number;
  rowIndex?: number;
};

/**
 * Renders controls in the tile-header location.
 *
 * All controls are rendered through ControlRenderer, which delegates to the
 * appropriate control component that handles its own rendering.
 *
 * @param props - Tile header slot props
 * @returns Rendered tile header or null if hidden/empty
 */
export const TileHeaderSlot: React.FC<TileHeaderSlotProps> = ({
  controls,
  cell,
  disableRunLinks,
  config,
  updateConfig,
  stepMeta,
  columnIndex,
  rowIndex,
}) => {
  // Don't render if cell is empty
  if (cell.isEmpty) {
    return null;
  }

  // Don't render tile titles for overlay tiles (masks/bounding boxes)
  if (cell.isOverlayTile) {
    return null;
  }

  return (
    <>
      {controls.map((control, idx) => (
        <ControlRenderer
          // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
          key={`tile-header-control-${idx}`}
          control={control}
          index={idx}
          config={config}
          updateConfig={updateConfig}
          stepMeta={stepMeta}
          columnIndex={columnIndex}
          rowIndex={rowIndex}
          disableRunLinks={disableRunLinks}
        />
      ))}
    </>
  );
};
